import { cn } from '@/lib/utils'
import type { CardData } from '../types'

interface CardProps {
  card: CardData
  isSelected?: boolean
  onClick?: () => void
  hideCost?: boolean
}

interface StatShieldProps {
  value: number | string
  type: 'cost' | 'power' | 'health'
  className?: string
}

function StatShield({ value, type, className }: StatShieldProps) {
  const styles = {
    cost: 'border-white/20 bg-white/10 text-white rounded-full',
    power: 'border-white/10 bg-black/40 text-white rounded-md shadow-inner',
    health: 'border-red-500/20 bg-red-950/40 text-red-500 rounded-md shadow-inner',
  }

  return (
    <div
      className={cn(
        'w-6 h-6 border flex items-center justify-center font-black tabular-nums text-[10px] backdrop-blur-md',
        styles[type],
        className,
      )}
    >
      {value}
    </div>
  )
}

export function Card({ card, isSelected = false, onClick, hideCost = false }: CardProps) {
  const content = (
    <div className="relative w-full h-full p-2 flex flex-col group/card-inner">
      {/* 1. Stat Shields */}
      {!hideCost && (
        <StatShield value={card.cost} type="cost" className="absolute -top-1 -left-1 z-30" />
      )}

      {card.power !== null && card.health !== null && card.type === 'creature' && (
        <>
          <StatShield value={card.power} type="power" className="absolute -bottom-1 -left-1 z-30" />
          <StatShield
            value={card.health}
            type="health"
            className="absolute -bottom-1 -right-1 z-30"
          />
        </>
      )}

      {/* 2. Art Layer */}
      <div className="w-full aspect-square relative overflow-hidden bg-[#0a0c10] border border-white/5 rounded-sm shrink-0">
        {card.imageUrl ? (
          <img
            src={card.imageUrl}
            alt={card.name}
            className="w-full h-full object-cover opacity-90 group-hover/card-inner:opacity-100 transition-opacity"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center opacity-10">
            <span className="text-2xl font-black font-theme">{card.name[0]}</span>
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
      </div>

      {/* 3. Text Area (The "Textbox") */}
      <div className="flex-1 relative z-10 p-2 pt-2.5 flex flex-col gap-1 bg-white/[0.03] backdrop-blur-sm border-t border-white/5 overflow-hidden">
        <h3 className="text-[9px] font-theme text-white tracking-widest truncate uppercase leading-none">
          {card.name}
        </h3>
        <p className="text-[8px] font-theme-body text-white/50 leading-tight">
          {card.description}
        </p>
      </div>
      {/* 4. Unified Keyword System (Colored Outlines) */}
      <div className="absolute inset-0 pointer-events-none z-20">
        {card.keywords.includes('guard') && (
          <div className="absolute inset-0 border-[3px] border-white/10" />
        )}
        {card.keywords.includes('ward') && (
          <div className="absolute inset-0 border border-cyan-400/40 shadow-[inset_0_0_10px_rgba(34,211,238,0.2)]" />
        )}
        {card.keywords.includes('lethal') && (
          <div className="absolute inset-0 border border-lime-400/40 shadow-[inset_0_0_10px_rgba(163,230,53,0.2)]" />
        )}
        {card.keywords.includes('charge') && (
          <div className="absolute inset-0 border border-yellow-400/40 shadow-[inset_0_0_10px_rgba(250,204,21,0.2)]" />
        )}
        {card.keywords.includes('drain') && (
          <div className="absolute inset-0 border border-rose-500/40 shadow-[inset_0_0_10px_rgba(244,63,94,0.2)]" />
        )}
      </div>
    </div>
  )

  const Container = onClick ? 'button' : 'div'

  return (
    <Container
      type={onClick ? 'button' : undefined}
      className={cn(
        'w-full h-full pro-card-bg relative transition-all duration-300 border border-white/10 shadow-lg',
        onClick && 'cursor-pointer active:scale-95 hover:border-white/20',
        isSelected && 'z-50 scale-105 border-white shadow-[0_0_30px_rgba(255,255,255,0.1)]',
      )}
      onClick={onClick}
    >
      <div className="absolute inset-1 border border-white/5 pointer-events-none" />
      {content}
    </Container>
  )
}

interface CardSlotProps {
  position: number
  creature?: {
    name: string
    power: number
    health: number
  }
  isEmpty: boolean
  onCardClick?: () => void
}

export function CardSlot({ position, creature, isEmpty = true, onCardClick }: CardSlotProps) {
  return (
    <button
      type="button"
      className={cn(
        'w-[75px] h-[100px] border border-dashed border-white/10 flex items-center justify-center relative cursor-pointer',
        isEmpty ? 'bg-transparent' : 'bg-white/5',
        !isEmpty && 'border-solid border-white/20',
      )}
      onClick={onCardClick}
    >
      {isEmpty ? (
        <div className="flex flex-col items-center opacity-20">
          <div className="text-white text-xs">{position + 1}</div>
        </div>
      ) : creature ? (
        <div className="w-full h-full flex flex-col justify-between p-2">
          <div className="text-[9px] text-white font-theme tracking-wider text-center truncate uppercase">{creature.name}</div>
          <div className="flex justify-center gap-2 items-center">
            <span className="text-white text-[12px] font-black">{creature.power}</span>
            <span className="text-white/20 text-[10px]">/</span>
            <span className="text-red-500 text-[12px] font-black">{creature.health}</span>
          </div>
        </div>
      ) : null}
    </button>
  )
}
